import { useEffect, useState } from "react";
import { FileJson, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import GstReport from "../components/GstReport";
import { fmt } from "../utils";
import { useLang } from "../i18n";

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };

export default function Reports() {
  const [tab, setTab] = useState("sales");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [filingMonth, setFilingMonth] = useState(monthStart().slice(0, 7));
  const filingPeriod = filingMonth ? filingMonth.slice(5, 7) + filingMonth.slice(0, 4) : "";
  const { t } = useLang();

  const load = async (signal) => {
    setData(null);
    try {
      const params = { date_from: from, date_to: to };
      let next;
      if (tab === "sales" || tab === "purchase") {
        next = { type: "register", ...(await api.get("/reports/register", { params: { kind: tab, ...params }, signal })).data };
      } else if (tab === "gstr1") {
        next = { type: "gstr1", ...(await api.get("/reports/gstr1", { params: { period: filingPeriod }, signal })).data };
      } else {
        const endpoint = { pnl: "pnl", cashflow: "cashflow", balance: "balancesheet" }[tab];
        next = { type: tab, ...(await api.get(`/reports/${endpoint}`, { params, signal })).data };
      }
      if (!signal.aborted) setData(next);
    } catch (error) { if (!signal.aborted) setData({ type: "error", message: error.message }); }
  };
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, from, to, filingPeriod]);

  const downloadFile = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob", params: path === "/reports/gstr1-json" ? { period: filingPeriod } : { date_from: from, date_to: to } });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (err) { toast.error(err.message); }
  };

  const tabs = [
    ["sales", "Sales Register"], ["purchase", "Purchase Register"], ["gstr1", "GSTR-1 Summary"],
    ["pnl", "Profit & Loss"], ["cashflow", "Cash Flow"], ["balance", "Balance Sheet"],
  ];

  const Th = ({ children, right }) => <th className={`sgp-th ${right ? "text-right" : ""}`}>{children}</th>;

  return (
    <div data-testid="reports-page" className="sgp-page">
      <div>
        <h1 className="sgp-page-title">{t("reports")}</h1>
        <p className="sgp-page-subtitle">Registers, GSTR-1 data, P&L, Balance Sheet — CA ke liye ready.</p>
      </div>

      <div className="sgp-toolbar">
        {tabs.map(([k, label]) => (
          <button key={k} data-testid={`report-tab-${k}`} onClick={() => setTab(k)}
            className={`sgp-tab ${tab === k ? "sgp-tab-active" : ""}`}>
            {label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 text-xs">
          {tab === "gstr1" && <button data-testid="gstr1-json-btn" disabled={!filingPeriod || data?.type !== "gstr1" || !!data?.validation_errors?.length} onClick={() => downloadFile("/reports/gstr1-json", `gstr1_${filingPeriod}.json`)}
            className="sgp-secondary-btn h-8 px-3 text-xs">
            <FileJson className="h-3.5 w-3.5" /> GSTR-1 JSON (GST Portal)
          </button>}
          <button data-testid="datev-btn" onClick={() => downloadFile("/reports/datev", "datev_export.csv")}
            className="sgp-secondary-btn h-8 px-3 text-xs">
            <FileSpreadsheet className="h-3.5 w-3.5" /> DATEV Export (CA Import)
          </button>
          {tab === "gstr1" && <label className="text-xs">Filing month<input data-testid="gst-filing-month" type="month" required className="sgp-input w-40 font-mono" value={filingMonth} onChange={e => setFilingMonth(e.target.value)} /></label>}
          {tab !== "balance" && tab !== "gstr1" && (
            <>
              <input data-testid="report-from" type="date" className="sgp-input w-36 font-mono" value={from} onChange={(e) => setFrom(e.target.value)} />
              <span className="text-slate-400">to</span>
              <input data-testid="report-to" type="date" className="sgp-input w-36 font-mono" value={to} onChange={(e) => setTo(e.target.value)} />
            </>
          )}
        </div>
      </div>

      {!data ? <p className="text-sm text-slate-400 py-10 text-center">Loading report...</p> : (
        <div data-testid="report-content" className="space-y-4">
          {data.type === "error" && <p role="alert" className="sgp-card p-4 text-rose-700">{data.message}</p>}
          {data.type === "register" && (
            <div className="sgp-table-shell overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr><Th>Bill No.</Th><Th>Date</Th><Th>{tab === "sales" ? "Hospital" : "Company"}</Th>
                    <Th right>Taxable</Th><Th right>CGST</Th><Th right>SGST</Th><Th right>IGST</Th><Th right>Grand Total</Th></tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="sgp-td font-mono text-xs">{r.bill_no}</td>
                      <td className="sgp-td font-mono text-xs">{r.date}</td>
                      <td className="sgp-td">{tab === "sales" ? r.hospital_name : r.company_name}</td>
                      <td className="sgp-td text-right font-mono text-xs">{fmt(r.taxable_total)}</td>
                      <td className="sgp-td text-right font-mono text-xs">{fmt(r.cgst_total)}</td>
                      <td className="sgp-td text-right font-mono text-xs">{fmt(r.sgst_total)}</td>
                      <td className="sgp-td text-right font-mono text-xs">{fmt(r.igst_total)}</td>
                      <td className="sgp-td text-right font-mono text-xs font-semibold">{fmt(r.grand_total)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-semibold">
                    <td className="sgp-td" colSpan={3}>TOTAL ({data.rows.length} bills)</td>
                    <td className="sgp-td text-right font-mono text-xs" data-testid="register-total-taxable">{fmt(data.totals.taxable)}</td>
                    <td className="sgp-td text-right font-mono text-xs">{fmt(data.totals.cgst)}</td>
                    <td className="sgp-td text-right font-mono text-xs">{fmt(data.totals.sgst)}</td>
                    <td className="sgp-td text-right font-mono text-xs">{fmt(data.totals.igst)}</td>
                    <td className="sgp-td text-right font-mono text-xs" data-testid="register-total-grand">{fmt(data.totals.grand)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {data.type === "gstr1" && <GstReport data={data} />}

          {data.type === "pnl" && (
            <div className="sgp-card p-6 max-w-md space-y-2 text-sm" data-testid="pnl-card">
              <h3 className="font-heading font-bold text-slate-800 text-lg mb-3">Profit & Loss ({from} → {to})</h3>
              <div className="flex justify-between"><span className="text-slate-500">Sales (Taxable Revenue)</span><span className="font-mono font-semibold" data-testid="pnl-revenue">{fmt(data.revenue)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Cost of Goods Sold</span><span className="font-mono" data-testid="pnl-cogs">({fmt(data.cogs)})</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
                <span>Gross Profit</span><span className="font-mono text-teal-700" data-testid="pnl-profit">{fmt(data.gross_profit)}</span>
              </div>
              <div className="flex justify-between text-xs"><span className="text-slate-400">Margin</span><span className="font-mono" data-testid="pnl-margin">{data.margin_pct}%</span></div>
            </div>
          )}

          {data.type === "cashflow" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="sgp-table-shell overflow-x-auto">
                <h3 className="px-4 pt-4 font-heading font-bold text-emerald-700">Money In — {fmt(data.total_in)}</h3>
                <table className="w-full mt-2">
                  <thead><tr><Th>Date</Th><Th>From</Th><Th>Ref</Th><Th right>Amount</Th></tr></thead>
                  <tbody data-testid="cashflow-in">
                    {data.inflow.map((r, i) => (
                      <tr key={i}><td className="sgp-td font-mono text-xs">{r.date}</td><td className="sgp-td text-xs">{r.party}</td>
                        <td className="sgp-td font-mono text-xs">{r.ref} · {r.mode}</td><td className="sgp-td text-right font-mono text-xs text-emerald-700">{fmt(r.amount)}</td></tr>
                    ))}
                    {!data.inflow.length && <tr><td colSpan={4} className="sgp-td text-center py-6 text-slate-400">Koi payment received nahi.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="sgp-table-shell overflow-x-auto">
                <h3 className="px-4 pt-4 font-heading font-bold text-rose-600">Money Out — {fmt(data.total_out)}</h3>
                <table className="w-full mt-2">
                  <thead><tr><Th>Date</Th><Th>To</Th><Th>Ref</Th><Th right>Amount</Th></tr></thead>
                  <tbody data-testid="cashflow-out">
                    {data.outflow.map((r, i) => (
                      <tr key={i}><td className="sgp-td font-mono text-xs">{r.date}</td><td className="sgp-td text-xs">{r.party}</td>
                        <td className="sgp-td font-mono text-xs">{r.ref} · {r.mode}</td><td className="sgp-td text-right font-mono text-xs text-rose-600">{fmt(r.amount)}</td></tr>
                    ))}
                    {!data.outflow.length && <tr><td colSpan={4} className="sgp-td text-center py-6 text-slate-400">Koi payment made nahi.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="sgp-card p-4 lg:col-span-2 flex justify-between text-sm font-bold">
                <span>Net Cash Flow</span><span data-testid="cashflow-net" className={`font-mono ${data.net >= 0 ? "text-teal-700" : "text-rose-600"}`}>{fmt(data.net)}</span>
              </div>
            </div>
          )}

          {data.type === "balance" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="balance-sheet">
              <div className="sgp-card p-6 space-y-2 text-sm">
                <h3 className="font-heading font-bold text-slate-800 text-lg mb-3">Assets</h3>
                <div className="flex justify-between"><span className="text-slate-500">Closing Stock (at cost)</span><span className="font-mono" data-testid="bs-stock">{fmt(data.assets.stock)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Sundry Debtors (Receivable)</span><span className="font-mono" data-testid="bs-receivable">{fmt(data.assets.receivable)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Cash / Bank Balance</span><span className="font-mono" data-testid="bs-cash">{fmt(data.assets.cash)}</span></div>
                <div className="flex justify-between border-t border-slate-200 pt-2 font-bold"><span>Total Assets</span><span className="font-mono" data-testid="bs-assets-total">{fmt(data.assets.total)}</span></div>
              </div>
              <div className="sgp-card p-6 space-y-2 text-sm">
                <h3 className="font-heading font-bold text-slate-800 text-lg mb-3">Liabilities & Capital</h3>
                <div className="flex justify-between"><span className="text-slate-500">Sundry Creditors (Payable)</span><span className="font-mono" data-testid="bs-payable">{fmt(data.liabilities.payable)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Capital (balancing figure)</span><span className="font-mono" data-testid="bs-capital">{fmt(data.liabilities.capital)}</span></div>
                <div className="flex justify-between border-t border-slate-200 pt-2 font-bold"><span>Total Liabilities</span><span className="font-mono" data-testid="bs-liab-total">{fmt(data.liabilities.total)}</span></div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
